import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { Modal, Spin, Button, Result, Empty, Typography, Space } from 'antd'
import { BarChartOutlined, ReloadOutlined, CalendarOutlined, EnvironmentOutlined } from '@ant-design/icons'
import { getAnalyticsForType } from './analyticsRegistry'
import './AnalyticsModal.css'

const { Text } = Typography

const RANGE_LABELS = {
  W: 'Weekly',
  M: 'Monthly',
  Y: 'Yearly',
  D: 'Daily',
}

const cleanHeader = (header) => (header ? header.trim().replace(/^["']|["']$/g, '') : '')

const parseCSV = (rawData) => {
  if (!rawData) return []
  if (Array.isArray(rawData)) {
    return rawData
  }
  if (typeof rawData !== 'string') return []

  const text = rawData.trim()
  if (!text) return []

  const rows = []
  let currentRow = []
  let currentVal = ''
  let insideQuotes = false

  for (let i = 0; i < text.length; i++) {
    const char = text[i]
    const nextChar = text[i + 1]

    if (insideQuotes) {
      if (char === '"' && nextChar === '"') {
        currentVal += '"'
        i++
      } else if (char === '"') {
        insideQuotes = false
      } else {
        currentVal += char
      }
    } else {
      if (char === '"') {
        insideQuotes = true
      } else if (char === ',') {
        currentRow.push(currentVal.trim())
        currentVal = ''
      } else if (char === '\r' && nextChar === '\n') {
        currentRow.push(currentVal.trim())
        currentVal = ''
        rows.push(currentRow)
        currentRow = []
        i++
      } else if (char === '\n' || char === '\r') {
        currentRow.push(currentVal.trim())
        currentVal = ''
        rows.push(currentRow)
        currentRow = []
      } else {
        currentVal += char
      }
    }
  }

  if (currentVal || currentRow.length > 0) {
    currentRow.push(currentVal.trim())
    rows.push(currentRow)
  }

  if (rows.length < 2) return []

  const headers = rows[0].map(cleanHeader)
  const data = []

  for (let i = 1; i < rows.length; i++) {
    const values = rows[i]
    if (values.length === 1 && values[0] === '') continue
    const rowObj = {}
    let hasData = false
    headers.forEach((h, idx) => {
      if (!h) return
      const val = values[idx] !== undefined ? values[idx] : ''
      rowObj[h] = val
      if (val !== '' && val !== null && val !== undefined) hasData = true
    })
    if (hasData) data.push(rowObj)
  }

  return data
}

class AnalyticsModal extends Component {
  renderHeader = (modalTitle, station, dateRange) => {
    const rangeKey = dateRange?.range || dateRange?.type
    const rangeLabel = RANGE_LABELS[rangeKey] || (typeof rangeKey === 'string' && rangeKey.length > 1 ? rangeKey : null)

    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          paddingRight: 40,
          flexWrap: 'wrap',
          gap: 8,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <BarChartOutlined
            style={{
              color: '#D02139',
              fontSize: 20,
            }}
          />
          <span
            style={{
              color: '#ffffff',
              fontWeight: 600,
              fontSize: 16,
            }}
          >
            {modalTitle}
          </span>
        </div>
        <Space size='small' className='analytics-header-meta'>
          {station && (
            <span className='analytics-modal-header-badge'>
              <EnvironmentOutlined style={{ marginRight: 4 }} />
              {station}
            </span>
          )}
          {rangeLabel && (
            <span className='analytics-modal-header-badge'>
              <CalendarOutlined style={{ marginRight: 4 }} />
              {rangeLabel}
            </span>
          )}
          {dateRange?.startDate && dateRange?.endDate && (
            <span className='analytics-modal-header-badge'>
              {dateRange.startDate} - {dateRange.endDate}
            </span>
          )}
        </Space>
      </div>
    )
  }

  renderContent = (loading, error, onRetry, registered, AnalyticsComponent, reportType, parsedData, dateRange, station) => {
    if (loading) {
      return (
        <div className='analytics-loading-state'>
          <Spin size='large' tip='Loading analytics data...' />
          <Text
            type='secondary'
            style={{
              marginTop: 16,
              fontSize: 13,
            }}
          >
            Processing report records and generating visualizations...
          </Text>
        </div>
      )
    }

    if (error) {
      return (
        <div className='analytics-error-state'>
          <Result
            status='error'
            title='Failed to Load Analytics'
            subTitle={typeof error === 'string' ? error : error?.message || 'An error occurred while fetching or processing the report data.'}
            extra={
              onRetry && (
                <Button type='primary' icon={<ReloadOutlined />} onClick={onRetry}>
                  Try Again
                </Button>
              )
            }
          />
        </div>
      )
    }

    if (!registered || !AnalyticsComponent) {
      return (
        <div className='analytics-empty-state'>
          <Empty
            description={`No analytics module registered for report type: "${reportType}"`}
          />
        </div>
      )
    }

    if (!parsedData || parsedData.length === 0) {
      return (
        <div className='analytics-empty-state'>
          <Empty
            description='No data available for the selected parameters.'
          />
        </div>
      )
    }

    return <AnalyticsComponent data={parsedData} dateRange={dateRange} station={station} />
  }

  render() {
    const {
      visible,
      onClose,
      reportType = 'users',
      data = null,
      loading = false,
      error = null,
      onRetry = null,
      dateRange = {},
      station = 'ALL',
      title = null,
    } = this.props

    const registered = getAnalyticsForType(reportType)
    const parsedData = parseCSV(data)
    const modalTitle = title || registered?.title || 'Report Analytics'
    const AnalyticsComponent = registered?.component

    return (
      <Modal
        title={this.renderHeader(modalTitle, station, dateRange)}
        open={visible}
        onCancel={onClose}
        footer={null}
        width='90%'
        style={{
          maxWidth: 1320,
          top: 24,
        }}
        className='analytics-modal'
        destroyOnClose
        centered
      >
        {this.renderContent(loading, error, onRetry, registered, AnalyticsComponent, reportType, parsedData, dateRange, station)}
      </Modal>
    )
  }
}

AnalyticsModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  reportType: PropTypes.string,
  data: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  loading: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  onRetry: PropTypes.func,
  dateRange: PropTypes.object,
  station: PropTypes.string,
  title: PropTypes.string,
}

export default AnalyticsModal
